import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Input, Result, Spin, App as AntdApp, Modal, Space, Dropdown, Select, Form, List, Empty, Collapse } from 'antd';
import type { MenuProps } from 'antd';
import { RefreshCw, Lock, Check, Download, FileText, AlertCircle, Calendar, Trash2, MoreVertical, UserMinus, UserCheck, Paperclip, Eye, CornerUpLeft } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { signingService } from '@/services/signingService';
import { attachmentService } from '@/services/attachmentService';
import { userService } from '@/services/userService';
import { useAuthStore } from '@/store/authStore';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { useBreakpoint } from '@/hooks/useBreakpoint';
// V1.0.40: PDF viewer cross-platform + Bình luận/Lịch sử
import { PdfFullViewer } from '@/components/signing/PdfFullViewer';
import { SigningCommentsHistory } from '@/components/signing/SigningCommentsHistory';
import { OfficeViewer } from '@/components/common/OfficeViewer';

export function SigningDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message, modal } = AntdApp.useApp();
  const { isMobile } = useBreakpoint();
  const currentUser = useAuthStore((s) => s.user);

  // Detect ngay từ đầu user có cần OTP không — tránh phải bấm "Chuẩn bị ký" rồi mới biết
  const userNeedsOtp = currentUser?.otpRequired !== false; // default TRUE nếu chưa biết

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(0);
  const [otpRequested, setOtpRequested] = useState(!userNeedsOtp); // auto-ready nếu không cần OTP
  const [otpSkipped, setOtpSkipped] = useState(!userNeedsOtp);
  const [maskedDest, setMaskedDest] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const inputRefs = useRef<(any)[]>([]);
  // V1.0.63e: Manage signer modal state
  const [manageSignerOpen, setManageSignerOpen] = useState(false);
  const [manageMode, setManageMode] = useState<'remove' | 'replace'>('remove');
  const [manageTargetUser, setManageTargetUser] = useState<{ id: string; fullName: string } | null>(null);
  const [manageReason, setManageReason] = useState('');
  const [manageNewSignerId, setManageNewSignerId] = useState<string | undefined>();
  const [manageUsers, setManageUsers] = useState<Array<{ id: string; fullName: string; department?: { name: string } }>>([]);
  const [manageSubmitting, setManageSubmitting] = useState(false);
  // V1.0.63i: Preview tài liệu liên quan / tài liệu trong task
  const [relatedPreviewAtt, setRelatedPreviewAtt] = useState<{ id: string; fileName: string; mimeType?: string | null } | null>(null);

  // V1.0.63e: Check user có quyền quản lý signers không
  const canManageSigners = currentUser?.permissions?.includes('signing:manage_signers')
    || currentUser?.roles?.includes('SYS_ADMIN')
    || false;

  const { data: sig, isLoading, refetch } = useQuery({
    queryKey: ['signing', id],
    queryFn: () => signingService.getById(id!),
    enabled: !!id,
  });

  const requestOtpMutation = useMutation({
    mutationFn: () => signingService.requestOtp(id!, 'EMAIL'),
    onSuccess: (data) => {
      // Backend trả "OTP_NOT_REQUIRED" nếu user config không cần OTP
      if (data.maskedDestination === 'OTP_NOT_REQUIRED') {
        setOtpSkipped(true);
        setOtpRequested(true);
        message.success('Tài khoản này không yêu cầu OTP. Bấm "Xác nhận & Ký" để ký ngay.');
        return;
      }
      setTimeLeft(data.expiresIn);
      setMaskedDest(data.maskedDestination);
      setOtpRequested(true);
      message.success('Đã gửi OTP tới email của bạn');
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message ?? err.message ?? 'Không gửi được OTP';
      message.error(msg);
    },
  });

  const signMutation = useMutation({
    mutationFn: () => signingService.sign(id!, otpSkipped ? '' : otp.join('')),
    onSuccess: async (data) => {
      message.success(`Ký thành công! Mã xác thực: ${data.sigCode}`);
      // Reset OTP để không hiện lại panel OTP
      setOtp(['', '', '', '', '', '']);
      // Refetch để load trạng thái mới (status=SIGNED, auto_created_document_id, signers updated)
      await refetch();
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message ?? err.message ?? 'Ký thất bại, kiểm tra lại OTP';
      message.error(msg);
      // Reset OTP để user nhập lại (chỉ khi user cần OTP)
      if (!otpSkipped) {
        setOtp(['', '', '', '', '', '']);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      }
    },
  });

  // V1.0.46: Xóa yêu cầu ký (chỉ creator + chưa ai ký)
  const deleteMutation = useMutation({
    mutationFn: () => signingService.delete(id!),
    onSuccess: () => {
      message.success('Đã xóa yêu cầu ký');
      navigate('/signing');
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Xóa thất bại'),
  });

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.warning('Vui lòng nhập lý do từ chối');
      return;
    }
    setRejecting(true);
    try {
      await signingService.reject(id!, rejectReason);
      message.success('Đã từ chối ký');
      setTimeout(() => navigate('/signing'), 1000);
    } catch (err: any) {
      message.error(err.response?.data?.message ?? err.message);
    } finally {
      setRejecting(false);
    }
  };

  useEffect(() => {
    if (timeLeft > 0) {
      const t = setTimeout(() => setTimeLeft((v) => v - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [timeLeft]);

  const handleOtpChange = (idx: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...otp];
    next[idx] = value;
    setOtp(next);
    if (value && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  // V1.0.63e: Mở modal Bỏ quyền ký
  const openRemoveSignerModal = async (user: { id: string; fullName: string }) => {
    setManageMode('remove');
    setManageTargetUser(user);
    setManageReason('');
    setManageSignerOpen(true);
  };

  // V1.0.63e: Mở modal Thay thế người ký
  const openReplaceSignerModal = async (user: { id: string; fullName: string }) => {
    setManageMode('replace');
    setManageTargetUser(user);
    setManageReason('');
    setManageNewSignerId(undefined);
    setManageSignerOpen(true);
    // Load danh sách user (lần đầu)
    if (manageUsers.length === 0) {
      try {
        const resp = await userService.list({ pageSize: 1000 });
        setManageUsers(resp.items.map(u => ({ id: u.id, fullName: u.fullName, department: u.department })));
      } catch (e: any) {
        message.error('Không tải được danh sách user');
      }
    }
  };

  const handleManageSubmit = async () => {
    if (!manageTargetUser || !id) return;
    if (manageMode === 'replace' && !manageNewSignerId) {
      message.warning('Vui lòng chọn người thay thế');
      return;
    }
    setManageSubmitting(true);
    try {
      if (manageMode === 'remove') {
        await signingService.removeSigner(id, manageTargetUser.id, manageReason || undefined);
        message.success(`Đã bỏ quyền ký của ${manageTargetUser.fullName}`);
      } else {
        const newUser = manageUsers.find(u => u.id === manageNewSignerId);
        await signingService.replaceSigner(
          id, manageTargetUser.id, manageNewSignerId!,
          manageReason || undefined,
        );
        message.success(`Đã thay ${manageTargetUser.fullName} bằng ${newUser?.fullName ?? 'người ký mới'}`);
      }
      setManageSignerOpen(false);
      refetch();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Có lỗi xảy ra');
    } finally {
      setManageSubmitting(false);
    }
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  if (isLoading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;
  if (!sig) return <Result status="404" title="Không tìm thấy yêu cầu ký" />;

  // Index của user hiện tại trong luồng ký (-1 nếu user không phải signer)
  // KHÔNG dùng sig.currentSignerIndex vì đó là signer ĐANG đến lượt, khác hoàn toàn với "tôi"
  const myIndex = currentUser?.id
    ? sig.signers.findIndex(s => s.user.id === currentUser.id)
    : -1;
  // Index của signer đang đến lượt (để highlight chấm vàng)
  const currentTurnIndex = sig.currentSignerIndex;

  // V1.0.63e: canSign - phải đúng cả PARALLEL lẫn SEQUENTIAL + multi-position
  // - User phải có ÍT NHẤT 1 row PENDING (chưa ký vị trí nào)
  // - PARALLEL: ai cũng có thể ký bất cứ lúc nào → chỉ cần check còn PENDING
  // - SEQUENTIAL: phải đến lượt - check MIN(order_index PENDING của user) === currentSignerIndex
  const myPendingRows = currentUser?.id
    ? sig.signers.filter(s => s.user.id === currentUser.id && s.status !== 'SIGNED' && s.status !== 'REJECTED')
    : [];
  const hasPendingForMe = myPendingRows.length > 0;
  const myMinPendingOrder = hasPendingForMe
    ? Math.min(...myPendingRows.map(s => s.order))
    : -1;
  const canSign = hasPendingForMe && (
    sig.workflow === 'PARALLEL' ||
    myMinPendingOrder === currentTurnIndex
  );

  // Lý do không ký được (hiển thị cho user biết)
  const cannotSignReason = (() => {
    if (myIndex < 0) return 'Bạn không phải người ký của yêu cầu này';
    if (sig.status && sig.status !== 'PENDING') return `Yêu cầu ký đang ở trạng thái ${sig.status}`;
    if (!hasPendingForMe) return 'Tất cả vị trí ký của bạn đã được xử lý';
    if (sig.workflow === 'SEQUENTIAL' && myMinPendingOrder !== currentTurnIndex) {
      const currentSigner = sig.signers.find(s => s.order === currentTurnIndex);
      return currentSigner
        ? `Chưa đến lượt bạn. Hiện đang đợi ${currentSigner.user.fullName} ký.`
        : 'Chưa đến lượt bạn.';
    }
    return null;
  })();

  const handleDownloadSigned = async () => {
    try {
      const blob = await signingService.getSignedPdf(id!);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fname = (sig?.documentName ?? 'document').replace(/\.[^.]+$/, '') + '_signed.pdf';
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Không tải được PDF đã ký. Có thể file không phải PDF hoặc chưa có ai ký.');
    }
  };

  return (
    <>
      <PageHeader
        breadcrumb={sig.documentId}
        title="Ký tài liệu"
        subtitle={`${sig.documentName} · Luồng ký ${sig.workflow === 'SEQUENTIAL' ? 'tuần tự' : 'song song'}`}
        extra={
          <Space>
            {sig.autoCreatedDocumentId && (
              <Button
                icon={<FileText size={14} />}
                onClick={() => navigate(`/documents/${sig.autoCreatedDocumentId}`)}
              >
                Xem trong kho tài liệu
              </Button>
            )}
            {sig.signers.some(s => s.status === 'SIGNED') && (
              <Button
                type="primary"
                icon={<Download size={14} />}
                onClick={handleDownloadSigned}
              >
                Tải PDF đã ký
              </Button>
            )}
            {/* V1.0.46: Xóa yêu cầu ký — chỉ creator + chưa ai ký + PENDING */}
            {(() => {
              const isCreator = currentUser?.id && sig.createdBy === currentUser.id;
              const hasSignedSomebody = sig.signers.some(s => s.status === 'SIGNED');
              const canDelete = isCreator && !hasSignedSomebody && sig.status === 'PENDING';
              if (!canDelete) return null;
              return (
                <Button
                  danger
                  icon={<Trash2 size={14} />}
                  loading={deleteMutation.isPending}
                  onClick={() => {
                    modal.confirm({
                      title: 'Xóa yêu cầu ký?',
                      content: (
                        <div>
                          <div>Yêu cầu ký <b>{sig.documentName}</b> sẽ bị xóa hoàn toàn.</div>
                          <div style={{ marginTop: 8, color: '#f5222d', fontSize: 12 }}>
                            ⚠ Thao tác này không thể hoàn tác. Chỉ xóa được khi chưa có ai ký.
                          </div>
                        </div>
                      ),
                      okText: 'Xóa hẳn',
                      okButtonProps: { danger: true },
                      cancelText: 'Quay lại',
                      onOk: () => deleteMutation.mutate(),
                    });
                  }}
                >
                  Xóa yêu cầu ký
                </Button>
              );
            })()}
          </Space>
        }
      />

      {sig.dueDate && sig.status === 'PENDING' && (() => {
        const due = dayjs(sig.dueDate);
        const isOverdue = due.isBefore(dayjs());
        return (
          <div style={{
            background: isOverdue ? '#fff1f0' : '#fff7e6',
            border: `1px solid ${isOverdue ? '#ffa39e' : '#ffd591'}`,
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
          }}>
            {isOverdue
              ? <AlertCircle size={16} style={{ color: '#ff4d4f', flexShrink: 0 }} />
              : <Calendar size={16} style={{ color: '#fa8c16', flexShrink: 0 }} />
            }
            <div style={{ flex: 1 }}>
              <b>{isOverdue ? 'Đã quá hạn ký' : 'Thời hạn ký'}:</b>{' '}
              {due.format('DD/MM/YYYY HH:mm')}{' '}
              <span style={{ color: '#8a95a5' }}>
                ({isOverdue
                  ? `quá ${dayjs().diff(due, 'hour')} giờ`
                  : `còn ${due.diff(dayjs(), 'hour')} giờ`})
              </span>
            </div>
          </div>
        );
      })()}

      {sig.status === 'SIGNED' && sig.autoCreatedDocumentId && (
        <div style={{
          background: '#e6f7e9',
          border: '1px solid #b7eb8f',
          borderRadius: 6,
          padding: '10px 14px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
        }}>
          <Check size={16} style={{ color: '#52c41a', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <b>Đã hoàn thành ký.</b> Tài liệu đã tự động lưu trữ trong kho EDM.
          </div>
          <Button size="small" type="link"
            onClick={() => navigate(`/documents/${sig.autoCreatedDocumentId}`)}>
            Mở tài liệu →
          </Button>
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,2fr) minmax(320px, 1fr)',
        gap: 16,
        minHeight: isMobile ? 'auto' : 600,
      }}>
        {/* PDF preview - real file từ server */}
        <div
          style={{
            background: '#eceef1',
            padding: isMobile ? 8 : 12,
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            minHeight: isMobile ? 400 : 600,
          }}
        >
          <RealPdfPreview
            requestId={id!}
            documentName={sig.documentName}
            hasSignedVersion={sig.signers.some((s: any) => s.status === 'SIGNED')}
          />
        </div>

        {/* Signing panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* V1.0.46: Block mô tả yêu cầu ký — hiển thị title + task_description */}
          {(sig.title || sig.taskDescription || sig.categoryName) && (
            <Card title="Thông tin yêu cầu ký">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {sig.title && (
                  <div>
                    <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600, marginBottom: 4 }}>
                      TIÊU ĐỀ
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#1e293b' }}>
                      {sig.title}
                    </div>
                  </div>
                )}
                {sig.taskDescription && (
                  <div>
                    <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600, marginBottom: 4 }}>
                      MÔ TẢ
                    </div>
                    <div style={{
                      fontSize: 13, color: '#48535e', lineHeight: 1.5,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {sig.taskDescription}
                    </div>
                  </div>
                )}
                {sig.categoryName && (
                  <div>
                    <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600, marginBottom: 4 }}>
                      LOẠI VĂN BẢN
                    </div>
                    <div style={{ fontSize: 13, color: '#48535e' }}>
                      {sig.categoryName}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          <Card title={`Luồng ký (${(() => {
            // V1.0.63d: Gộp theo user.id — 1 user nhiều vị trí thì chỉ tính 1 người
            const uniqueUsers = new Map<string, { signed: number; total: number }>();
            for (const s of sig.signers) {
              const cur = uniqueUsers.get(s.user.id) ?? { signed: 0, total: 0 };
              cur.total += 1;
              if (s.status === 'SIGNED') cur.signed += 1;
              uniqueUsers.set(s.user.id, cur);
            }
            const totalPeople = uniqueUsers.size;
            const fullySignedPeople = Array.from(uniqueUsers.values())
              .filter(u => u.signed === u.total).length;
            return `${fullySignedPeople}/${totalPeople}`;
          })()})`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(() => {
                // V1.0.63d: Gộp signers theo user.id để hiển thị 1 dòng/user
                // Group rows cùng user — giữ thông tin vị trí ký và status tổng hợp
                interface AggregatedSigner {
                  userId: string;
                  user: typeof sig.signers[0]['user'];
                  positions: typeof sig.signers;
                  minOrder: number;
                  signedCount: number;
                  totalCount: number;
                  status: 'ALL_SIGNED' | 'PARTIAL' | 'PENDING' | 'REJECTED';
                  earliestSignedAt: string | null;
                }
                const grouped = new Map<string, AggregatedSigner>();
                sig.signers.forEach((s, originalIdx) => {
                  const existing = grouped.get(s.user.id);
                  if (existing) {
                    existing.positions.push(s);
                    if (s.status === 'SIGNED') existing.signedCount += 1;
                    existing.totalCount += 1;
                    if (s.signedAt && (!existing.earliestSignedAt || s.signedAt < existing.earliestSignedAt)) {
                      existing.earliestSignedAt = s.signedAt;
                    }
                  } else {
                    grouped.set(s.user.id, {
                      userId: s.user.id,
                      user: s.user,
                      positions: [s],
                      minOrder: originalIdx,
                      signedCount: s.status === 'SIGNED' ? 1 : 0,
                      totalCount: 1,
                      status: s.status === 'REJECTED' ? 'REJECTED' : (s.status === 'SIGNED' ? 'ALL_SIGNED' : 'PENDING'),
                      earliestSignedAt: s.signedAt ?? null,
                    });
                  }
                });
                // Update status tổng hợp sau khi đã group
                grouped.forEach(g => {
                  if (g.positions.some(p => p.status === 'REJECTED')) g.status = 'REJECTED';
                  else if (g.signedCount === g.totalCount) g.status = 'ALL_SIGNED';
                  else if (g.signedCount > 0) g.status = 'PARTIAL';
                  else g.status = 'PENDING';
                });
                // Sort theo minOrder để giữ thứ tự gốc
                const groupedList = Array.from(grouped.values()).sort((a, b) => a.minOrder - b.minOrder);

                return groupedList.map((g, displayIdx) => {
                  // Current turn: chỉ tính row đầu PENDING trong sig.signers
                  const myFirstRowIdx = sig.signers.findIndex(s => s.user.id === g.userId);
                  const isCurrentTurn = myFirstRowIdx === currentTurnIndex && g.status !== 'ALL_SIGNED';
                  const isMe = sig.signers.findIndex(s => s.user.id === g.userId) === myIndex
                    || sig.signers.some((s, i) => s.user.id === g.userId && i === myIndex);
                  // Đơn giản hóa: check userId hiện tại
                  const myUserId = sig.signers[myIndex]?.user.id;
                  const isMeFinal = myUserId === g.userId;
                  return (
                <div key={g.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background:
                        g.status === 'ALL_SIGNED' ? '#6699CC'
                        : g.status === 'REJECTED' ? '#cf1322'
                        : g.status === 'PARTIAL' ? '#fa8c16'
                        : isCurrentTurn ? '#f59e0b' : '#d4d9df',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      flexShrink: 0,
                    }}
                  >
                    {g.status === 'ALL_SIGNED' ? '✓'
                      : g.status === 'REJECTED' ? '✕'
                      : isCurrentTurn ? '●'
                      : displayIdx + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: isMeFinal ? 600 : 400 }}>
                      {g.user.fullName}
                      {isMeFinal && <span style={{ color: '#6699CC', fontSize: 11, marginLeft: 4 }}>(bạn)</span>}
                      {/* V1.0.63d: Số vị trí ký - chỉ hiện khi > 1 */}
                      {g.totalCount > 1 && (
                        <span style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 600,
                          padding: '1px 6px', borderRadius: 8,
                          background: g.status === 'ALL_SIGNED' ? '#e6f4ff' : '#fff7e6',
                          color: g.status === 'ALL_SIGNED' ? '#0958d9' : '#d46b08',
                          border: `1px solid ${g.status === 'ALL_SIGNED' ? '#91d5ff' : '#ffd591'}`,
                        }}>
                          {g.signedCount}/{g.totalCount} vị trí
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: '#adb6c0' }}>{g.user.position}</div>
                  </div>
                  {g.status === 'ALL_SIGNED' && g.earliestSignedAt && (
                    <span className="mono" style={{ fontSize: 10, color: '#adb6c0' }}>
                      {new Date(g.earliestSignedAt).toLocaleDateString('vi-VN')}
                    </span>
                  )}
                  {/* V1.0.63e: Action menu cho signer còn PENDING - chỉ user có quyền thấy */}
                  {canManageSigners
                    && sig.status === 'PENDING'
                    && g.status !== 'ALL_SIGNED'
                    && g.status !== 'REJECTED' && (
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        items: [
                          {
                            key: 'replace',
                            label: 'Thay thế người ký',
                            icon: <UserCheck size={12} />,
                            onClick: () => openReplaceSignerModal({ id: g.userId, fullName: g.user.fullName }),
                          },
                          {
                            key: 'remove',
                            label: 'Bỏ quyền ký',
                            icon: <UserMinus size={12} />,
                            danger: true,
                            onClick: () => openRemoveSignerModal({ id: g.userId, fullName: g.user.fullName }),
                          },
                        ] as MenuProps['items'],
                      }}
                    >
                      <Button type="text" size="small" icon={<MoreVertical size={14} />}
                        style={{ color: '#7d8a98' }}
                        title="Quản lý người ký"
                      />
                    </Dropdown>
                  )}
                </div>
                  );
                });
              })()}
            </div>
          </Card>

          {/* V1.0.63i: Tài liệu tham chiếu + Tài liệu trong task — gộp vào Collapse, mặc định thu gọn */}
          {((sig.relatedAttachments && sig.relatedAttachments.length > 0) || sig.taskId) && (
            <Collapse
              size="small"
              ghost={false}
              style={{ background: '#fff', border: '1px solid #e1e5eb' }}
              items={[
                // Panel 1: Tài liệu tham chiếu
                ...(sig.relatedAttachments && sig.relatedAttachments.length > 0 ? [{
                  key: 'related',
                  label: (
                    <Space>
                      <Paperclip size={14} style={{ color: '#6699CC' }} />
                      <span style={{ fontWeight: 500 }}>
                        Tài liệu tham chiếu ({sig.relatedAttachments.length})
                      </span>
                    </Space>
                  ),
                  children: (
                    <>
                      <div style={{ fontSize: 11, color: '#8a95a5', marginBottom: 8 }}>
                        💡 Tài liệu đính kèm để tham chiếu trước khi ký (không phải tài liệu ký).
                      </div>
                      <List
                        size="small"
                        dataSource={sig.relatedAttachments}
                        renderItem={(a: any) => (
                          <List.Item
                            actions={[
                              <Button key="view" size="small" type="link" icon={<Eye size={13} />}
                                onClick={() => setRelatedPreviewAtt(a)}>
                                Xem
                              </Button>
                            ]}
                          >
                            <Space>
                              <FileText size={13} style={{ color: '#5b6875' }} />
                              <span style={{ fontWeight: 500 }}>{a.fileName}</span>
                              <span style={{ fontSize: 11, color: '#8a95a5' }}>
                                ({(a.fileSize / 1024).toFixed(1)} KB)
                              </span>
                              {a.addedByName && (
                                <span style={{ fontSize: 11, color: '#8a95a5' }}>· {a.addedByName}</span>
                              )}
                            </Space>
                          </List.Item>
                        )}
                      />
                    </>
                  ),
                }] : []),
                // Panel 2: Tài liệu trong task
                ...(sig.taskId ? [{
                  key: 'task-context',
                  label: (
                    <Space>
                      <FileText size={14} style={{ color: '#6699CC' }} />
                      <span style={{ fontWeight: 500 }}>Tài liệu trong task</span>
                    </Space>
                  ),
                  children: (
                    <TaskContextAttachments
                      taskId={sig.taskId}
                      taskCode={sig.taskCode}
                      taskTitle={sig.taskTitle}
                      mainAttachmentId={sig.documentId}
                      relatedIds={(sig.relatedAttachments ?? []).map((a: any) => a.id)}
                      onPreview={setRelatedPreviewAtt}
                    />
                  ),
                }] : []),
              ]}
            />
          )}

          {/* V1.0.40: Bình luận + Lịch sử ký — ngay dưới Card Luồng ký */}
          <SigningCommentsHistory requestId={id!} />

          {/* V1.0.63e: Hiển thị rõ lý do không ký được */}
          {cannotSignReason && (
            <div style={{
              padding: '10px 14px',
              background: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: 6,
              fontSize: 12,
              color: '#874d00',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
            }}>
              <span style={{ flexShrink: 0 }}>⚠</span>
              <span><b>Không thể ký:</b> {cannotSignReason}</span>
            </div>
          )}

          {/* V1.0.63i: Banner thông báo số vị trí ký cho người ký hiện tại */}
          {canSign && currentUser && (() => {
            const myPending = sig.signers.filter(s =>
              s.user.id === currentUser.id && s.status === 'PENDING'
            );
            if (myPending.length === 0) return null;
            const isMulti = myPending.length > 1;
            return (
              <div style={{
                marginBottom: 12,
                padding: '12px 14px',
                background: isMulti ? '#fffbe6' : '#e6f4ff',
                border: `1px solid ${isMulti ? '#ffd666' : '#91caff'}`,
                borderRadius: 6,
                fontSize: 13,
                lineHeight: 1.5,
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: isMulti ? '#ad6800' : '#0958d9' }}>
                  📍 Bạn có <span style={{ fontSize: 16 }}>{myPending.length}</span> vị trí cần ký
                  {isMulti && ' trên cùng tài liệu này'}
                </div>
                <div style={{ fontSize: 12, color: '#5b6875' }}>
                  {isMulti
                    ? <>
                        Khi bạn xác nhận ký, chữ ký sẽ được áp đồng thời vào <b>tất cả {myPending.length} vị trí</b>.
                        Chỉ cần thao tác 1 lần để ký hết.
                      </>
                    : 'Bấm "Xác nhận & Ký" để xác nhận chữ ký lên tài liệu.'}
                </div>
              </div>
            );
          })()}

          {!otpRequested ? (
            <Button
              type="primary"
              size="large"
              block
              icon={<Lock size={14} />}
              onClick={() => requestOtpMutation.mutate()}
              loading={requestOtpMutation.isPending}
              disabled={!canSign}
            >
              Chuẩn bị ký
            </Button>
          ) : otpSkipped ? (
            // V1.0.63i: User không cần OTP → không hiển thị panel nữa, chỉ giữ nút "Xác nhận & Ký" bên dưới
            null
          ) : (
            <Card
              title={
                <span>
                  <Lock size={14} style={{ verticalAlign: -2, marginRight: 6, color: '#6699CC' }} />
                  Xác thực OTP
                </span>
              }
            >
              <div style={{ fontSize: 12, color: '#48535e', marginBottom: 12, lineHeight: 1.6 }}>
                Mã OTP đã gửi tới <b>{maskedDest}</b>.<br />
                Hiệu lực: <b style={{ color: timeLeft < 60 ? '#dc2626' : '#6699CC' }} className="mono">{formatTime(timeLeft)}</b>
              </div>

              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {otp.map((digit, idx) => (
                  <Input
                    key={idx}
                    ref={(el) => (inputRefs.current[idx] = el)}
                    value={digit}
                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(idx, e)}
                    maxLength={1}
                    style={{
                      width: '100%',
                      height: 44,
                      textAlign: 'center',
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 18,
                      fontWeight: 600,
                    }}
                  />
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <Button
                  type="link"
                  icon={<RefreshCw size={11} />}
                  size="small"
                  onClick={() => requestOtpMutation.mutate()}
                  disabled={timeLeft > 240}
                  style={{ padding: 0 }}
                >
                  Gửi lại mã
                </Button>
                <a style={{ color: '#7d8a98' }}>Gửi qua SMS thay thế</a>
              </div>
            </Card>
          )}

          <div
            style={{
              background: '#f6f7f8',
              padding: 12,
              borderRadius: 6,
              fontSize: 11,
              color: '#5b6875',
              lineHeight: 1.6,
            }}
          >
            <b>Xác nhận:</b> Tôi đồng ý rằng việc ký này có giá trị pháp lý theo Quy chế chữ ký điện tử nội bộ BV Hưng Việt. Hệ thống sẽ ghi nhận IP, thời điểm, hash tài liệu.
          </div>

          <Button
            type="primary"
            size="large"
            block
            icon={<Check size={14} />}
            disabled={!otpRequested || (!otpSkipped && otp.some((d) => !d)) || !canSign}
            loading={signMutation.isPending}
            onClick={() => signMutation.mutate()}
          >
            Xác nhận & Ký
          </Button>
          <Button size="large" block danger onClick={() => setRejectOpen(true)}>
            Từ chối ký
          </Button>
        </div>
      </div>

      <Modal
        title="Từ chối ký tài liệu"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={handleReject}
        okText="Từ chối"
        cancelText="Hủy"
        confirmLoading={rejecting}
        okButtonProps={{ danger: true }}
      >
        <p style={{ color: '#ff6b6b', fontSize: 13 }}>
          ⚠ Từ chối ký sẽ hủy toàn bộ luồng ký này. Người gửi sẽ được thông báo kèm lý do.
        </p>
        <Input.TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Nhập lý do từ chối (VD: Tài liệu thiếu điều khoản X, cần bổ sung thông tin Y...)"
        />
      </Modal>

      {/* V1.0.63e: Modal Quản lý người ký (Bỏ / Thay thế) */}
      <Modal
        title={manageMode === 'remove' ? 'Bỏ quyền ký' : 'Thay thế người ký'}
        open={manageSignerOpen}
        onCancel={() => setManageSignerOpen(false)}
        onOk={handleManageSubmit}
        okButtonProps={{ danger: manageMode === 'remove', loading: manageSubmitting }}
        okText={manageMode === 'remove' ? 'Bỏ quyền ký' : 'Xác nhận thay thế'}
        cancelText="Hủy"
        width={500}
      >
        {manageTargetUser && (
          <Form layout="vertical">
            <Form.Item label="Người ký hiện tại">
              <Input value={manageTargetUser.fullName} disabled />
            </Form.Item>

            {manageMode === 'replace' && (
              <Form.Item label="Thay bằng" required>
                <Select
                  showSearch
                  placeholder="Chọn người ký thay thế..."
                  optionFilterProp="label"
                  value={manageNewSignerId}
                  onChange={setManageNewSignerId}
                  options={manageUsers
                    .filter(u => u.id !== manageTargetUser.id)
                    .map(u => ({
                      value: u.id,
                      label: `${u.fullName}${u.department ? ` — ${u.department.name}` : ''}`,
                    }))}
                />
              </Form.Item>
            )}

            <Form.Item label="Lý do (tuỳ chọn)">
              <Input.TextArea
                rows={3}
                value={manageReason}
                onChange={(e) => setManageReason(e.target.value)}
                placeholder={manageMode === 'remove'
                  ? "VD: User đã chuyển công tác, không cần ký nữa..."
                  : "VD: User cũ đang nghỉ phép, ủy quyền cho user mới..."}
                maxLength={500}
                showCount
              />
            </Form.Item>

            <div style={{
              padding: '8px 12px', background: '#fff7e6',
              border: '1px solid #ffd591', borderRadius: 6, fontSize: 12, color: '#874d00',
            }}>
              {manageMode === 'remove'
                ? '⚠ Tất cả vị trí ký đang chờ của user này sẽ bị xóa khỏi file. Hành động không thể hoàn tác.'
                : '⚠ Tất cả vị trí ký đang chờ sẽ được chuyển sang người mới. Vị trí và rect trên file giữ nguyên.'}
            </div>
          </Form>
        )}
      </Modal>

      {/* V1.0.63i: Modal preview tài liệu liên quan / tài liệu trong task */}
      {relatedPreviewAtt && (
        <RelatedAttachmentPreviewModal
          attachment={relatedPreviewAtt}
          onClose={() => setRelatedPreviewAtt(null)}
        />
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════
// V1.0.63i: Card hiển thị tài liệu trong task (context cho người ký)
// ════════════════════════════════════════════════════════════
function TaskContextAttachments({ taskId, taskCode, taskTitle, mainAttachmentId, relatedIds, onPreview }: {
  taskId: string;
  taskCode?: string | null;
  taskTitle?: string | null;
  mainAttachmentId: string;
  relatedIds: string[];
  onPreview: (a: { id: string; fileName: string; mimeType?: string | null }) => void;
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState<Array<{ id: string; fileName: string; fileSize: number; mimeType?: string | null }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await attachmentService.list(taskId);
        if (cancelled) return;
        // Loại file đang ký + file đã ở mục tài liệu liên quan
        const excludeIds = new Set([mainAttachmentId, ...relatedIds]);
        setItems(list.filter((a: any) => !excludeIds.has(a.id)).map((a: any) => ({
          id: a.id, fileName: a.fileName, fileSize: a.fileSize, mimeType: a.mimeType,
        })));
      } catch {
        // tolerate
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [taskId, mainAttachmentId, relatedIds.join(',')]);

  if (loading) return <Card title="Tài liệu trong task"><Spin /></Card>;
  if (items.length === 0) return null;

  return (
    <Card title={
      <Space>
        <FileText size={14} style={{ color: '#6699CC' }} />
        <span>Tài liệu khác trong task ({items.length})</span>
      </Space>
    } extra={
      <Button type="link" size="small" icon={<CornerUpLeft size={12} />}
        onClick={() => navigate(`/tasks/${taskId}`)}>
        Đến task
      </Button>
    }>
      {taskCode && taskTitle && (
        <div style={{ fontSize: 11, color: '#8a95a5', marginBottom: 12 }}>
          📁 Task: <b style={{ color: '#1976D2' }}>{taskCode}</b> — {taskTitle}
        </div>
      )}
      <List
        size="small"
        dataSource={items}
        renderItem={(a) => (
          <List.Item
            actions={[
              <Button key="view" size="small" type="link" icon={<Eye size={13} />}
                onClick={() => onPreview(a)}>
                Xem
              </Button>
            ]}
          >
            <Space>
              <FileText size={13} style={{ color: '#5b6875' }} />
              <span style={{ fontWeight: 500 }}>{a.fileName}</span>
              <span style={{ fontSize: 11, color: '#8a95a5' }}>
                ({(a.fileSize / 1024).toFixed(1)} KB)
              </span>
            </Space>
          </List.Item>
        )}
      />
    </Card>
  );
}

// ════════════════════════════════════════════════════════════
// V1.0.63i: Modal preview attachment (PDF + Office)
// ════════════════════════════════════════════════════════════
function RelatedAttachmentPreviewModal({ attachment, onClose }: {
  attachment: { id: string; fileName: string; mimeType?: string | null };
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const mime = attachment.mimeType ?? '';
  const isPdf = mime.includes('pdf') || attachment.fileName.toLowerCase().endsWith('.pdf');
  const isOffice = /\.(docx?|xlsx?|pptx?)$/i.test(attachment.fileName) || mime.includes('officedocument') || mime.includes('msword') || mime.includes('ms-excel');
  const isImage = mime.startsWith('image/');

  useEffect(() => {
    if (isOffice) {
      setLoading(false);
      return;
    }
    let url: string | null = null;
    let cancelled = false;
    attachmentService.previewBlob(attachment.id, attachment.mimeType ?? undefined, false)
      .then(u => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        url = u;
        setBlobUrl(u);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment.id]);

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  return (
    <Modal
      open
      title={<span><Eye size={14} style={{ verticalAlign: -2, marginRight: 8 }} />{attachment.fileName}</span>}
      onCancel={onClose}
      footer={null}
      width="90vw"
      style={{ top: 20, maxWidth: 1200 }}
      destroyOnClose
    >
      {isOffice ? (
        <div style={{ height: '85vh' }}>
          <OfficeViewer
            loadBlob={() => attachmentService.fetchBlob(attachment.id, false)}
            fileName={attachment.fileName}
            fileMime={attachment.mimeType ?? undefined}
            height="100%"
          />
        </div>
      ) : loading || !blobUrl ? (
        <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>
      ) : isPdf ? (
        <div style={{ height: '78vh' }}>
          <PdfFullViewer file={blobUrl} documentName={attachment.fileName} />
        </div>
      ) : isImage ? (
        <img src={blobUrl} alt={attachment.fileName} style={{ maxWidth: '100%', maxHeight: '78vh', display: 'block', margin: '0 auto' }} />
      ) : (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Empty description="Không hỗ trợ xem trực tiếp định dạng này" />
          <Button type="primary" style={{ marginTop: 16 }}
            onClick={() => attachmentService.download(attachment.id, attachment.fileName)}>
            Tải về máy
          </Button>
        </div>
      )}
    </Modal>
  );
}

// ============================================================
// Component hiển thị PDF thật từ API (không phải mock UI)
// ============================================================
function RealPdfPreview({ requestId, documentName, hasSignedVersion }: {
  requestId: string;
  documentName?: string;
  hasSignedVersion?: boolean;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(true);
  // Nếu đã có người ký, mặc định hiện bản signed; nếu chưa thì hiện original
  const [viewMode, setViewMode] = useState<'original' | 'signed'>(
    hasSignedVersion ? 'signed' : 'original'
  );

  useEffect(() => {
    let cancelled = false;
    let currentUrl: string | null = null;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const blob = viewMode === 'signed'
          ? await signingService.getSignedPdf(requestId)
          : await signingService.getOriginalPdf(requestId);
        if (cancelled) return;
        if (!blob.type.includes('pdf')) {
          setIsPdf(false);
        } else {
          setIsPdf(true);
        }
        currentUrl = URL.createObjectURL(blob);
        setBlobUrl(currentUrl);
      } catch (e: any) {
        if (cancelled) return;
        // Nếu đang load signed mà lỗi (chưa có file ký) → fallback original
        if (viewMode === 'signed') {
          setViewMode('original');
          return;
        }
        setError(e.response?.data?.message ?? e.message ?? 'Không tải được tài liệu');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [requestId, viewMode]);

  // V1.0.40: Toggle Original/Signed render thành button group, truyền vào PdfFullViewer header
  const viewToggle = hasSignedVersion ? (
    <div style={{ display: 'flex', gap: 0, border: '1px solid #d4d9df', borderRadius: 4, overflow: 'hidden' }}>
      <button
        onClick={() => setViewMode('original')}
        style={{
          padding: '3px 10px', border: 0, cursor: 'pointer', fontSize: 11,
          background: viewMode === 'original' ? '#6699CC' : '#fff',
          color: viewMode === 'original' ? '#fff' : '#5b6875',
        }}>
        Bản gốc
      </button>
      <button
        onClick={() => setViewMode('signed')}
        style={{
          padding: '3px 10px', border: 0, cursor: 'pointer', fontSize: 11,
          background: viewMode === 'signed' ? '#6699CC' : '#fff',
          color: viewMode === 'signed' ? '#fff' : '#5b6875',
        }}>
        Bản đã ký
      </button>
    </div>
  ) : null;

  const docDisplayName = viewMode === 'signed'
    ? `${documentName ?? 'Tài liệu'} (ĐÃ KÝ)`
    : (documentName ?? 'Tài liệu');

  // Loading state (PdfFullViewer cũng có loading riêng, nhưng đây là khi load blob từ API)
  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, minHeight: 500, background: '#fff', borderRadius: 6 }}>
        <Spin tip="Đang tải tài liệu..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, minHeight: 500, background: '#fff', borderRadius: 6 }}>
        <div style={{ textAlign: 'center', color: '#ff4d4f' }}>
          <div style={{ fontSize: 14, marginBottom: 8 }}>⚠ Không tải được tài liệu</div>
          <div style={{ fontSize: 12, color: '#8a95a5' }}>{error}</div>
        </div>
      </div>
    );
  }

  if (!isPdf) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, minHeight: 500, background: '#fff', borderRadius: 6 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>{documentName ?? 'Tài liệu'}</div>
          <div style={{ fontSize: 12, color: '#8a95a5', marginBottom: 16 }}>
            File không phải PDF — không hỗ trợ xem trước trực tiếp
          </div>
          <Button type="primary" icon={<Download size={14} />}
            onClick={() => {
              if (!blobUrl) return;
              const a = document.createElement('a');
              a.href = blobUrl;
              a.download = documentName ?? 'document';
              a.click();
            }}>
            Tải xuống để xem
          </Button>
        </div>
      </div>
    );
  }

  // V1.0.40: Dùng PdfFullViewer (pdf.js canvas) — hoạt động trên iPad/iOS
  return (
    <PdfFullViewer
      file={blobUrl}
      documentName={docDisplayName}
      extraHeaderRight={viewToggle}
    />
  );
}
